import { AsyncInformationModal } from '@/card/components/async-information-modal';
import { AddChartCard, Md as CardMD } from '@/card/components/card';
import { KPICard } from '@/card/components/kpi-card';
import { CHART_TO_COMPONENT } from '@/chart/components/all-charts';
import { AsyncRsModal } from '@/chart/components/async-rs-modal';
import Header from '@/chart/components/header';
import {
  MostActiveHosts,
  MostActiveOwners,
} from '@/chart/components/most-active-accounts';
import { dataTypeKeyNameMap } from '@/constants';
import { DashContentActionMenu } from '@/controls/components/action-menu/dash-content-action-menu';
import {
  getGridInstance,
  setNumGridItems,
} from '@/dashboard/muuri-grid-instance';
import * as dataActions from '@/data/data-actions';
import * as dataSelectors from '@/data/data-selectors';
import { useTransientOccupancyTaxName } from '@/hooks/useTaxName';
import { DataLoadingPlaceholder } from '@/widgets/components/data-loading-placeholder';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddChartModal } from './add-chart-modal';
import {
  DashCardListWrapper,
  Grid,
  GridWrapper,
} from './dash-card-list-styles';
import { PrintChartModal } from './print-chart-modal';
import * as exSidepanelItem from './side-panel-item';

// ------------------------------------------

// TODO: MOVE THESE COMPONENT DEFINITIONS TO THEIR OWN FILES ON REFACTOR OF CARDS AND CHARTS
// I will define these cards here because it will be easier to move this code on refactor
function RentalCategoriesCard({
  kpiData,
  onClickHandlers,
  handleHideShowCards,
  optionsOverride,
}) {
  const HIDE = true;
  const CARD_NAME = 'RENTAL_CATEGORIES';

  return (
    <CardMD
      onClickHandlers={onClickHandlers}
      onHide={() => handleHideShowCards([CARD_NAME], HIDE)}
      optionsOverride={optionsOverride}
    >
      <exSidepanelItem.SidePanelBody kpiData={kpiData} />
    </CardMD>
  );
}

function OnlinePlatformsCard({
  kpiData,
  onClickHandlers,
  handleHideShowCards,
  optionsOverride,
}) {
  const HIDE = true;
  const CARD_NAME = 'TOP_ONLINE_PLATFORMS';
  return (
    <CardMD
      title="Top Online Platforms"
      onClickHandlers={onClickHandlers}
      onHide={() => handleHideShowCards([CARD_NAME], HIDE)}
      optionsOverride={optionsOverride}
    >
      <exSidepanelItem.OnlinePlatformsSidepanelItem kpiData={kpiData} />
    </CardMD>
  );
}

// --------------------------------------------
/**
 * Movable charts are just that, charts that are movable
 */
const movableCharts = [
  'CURRENT_STR_PROPERTIES',
  'OCCUPANCY',
  'REVENUE',
  'AVG_DAILY_RATE',
  'TRANSIENT_OCCUPANCY_TAX',
  'RESIDENTIAL_TYPE',
  'RENTAL_STRUCTURE',
  'UNIT_TYPE',
  'RENTAL_SIZE',
  'ONLINE_PLATFORMS',
];
/* eslint-enable max-len */

const cardHeaderDropdownOptionsOverride = {
  KPI_CARD: { hide: false, expand: false },
  RENTAL_CATEGORIES: { expand: false },
  MOST_ACTIVE_HOSTS: { expand: false },
  MOST_ACTIVE_OWNERS: { expand: false },
  TOP_ONLINE_PLATFORMS: { expand: false },
};

function GridItem(props) {
  return (
    <div className="item" id={props.id}>
      <div className="item-content">{props.children}</div>
    </div>
  );
}

/**
 * Returns array of rendered cards that should be shown as the main dashboard content
 *
 * @param {object} props.kpiData
 * @returns {func} returns function to be used as onClick callback
 */
function KpiCards(props) {
  const { kpiData } = props;
  const [currActiveCardListModal, setCurrActiveCardListModal] = useState(null);
  const [clickTargetName, setClickTargetName] = useState(null);
  const [isAddChartModalVisible, setAddChartVisibility] = useState(false);
  const [isPrintChartModalVisible, setPrintChartVisibility] = useState(false);
  const [grid, setGrid] = useState(null);
  const totAlias = useTransientOccupancyTaxName();

  const handleHideShowCards = useCallback(
    (cardsToAffect, hide = false) => {
      let itemsAffected = [];
      // List of all current displayed items
      const allItems = grid.getItems();
      cardsToAffect.forEach((cardId) => {
        // Returns array of matching cardElements
        const found = allItems.filter(
          (item) => item.getElement().getAttribute('id') === cardId
        );
        // Append the found items to a total list of items to hide
        itemsAffected = itemsAffected.concat(found);
      });
      // Hide each item in the grid
      if (hide) {
        grid.hide(itemsAffected);
      } else {
        grid.show(itemsAffected);
      }
    },
    [grid]
  );

  /**
   * Movable Cards are all the entities that are not charts
   */
  const movableCards = {
    KPI_CARD: (
      <KPICard
        key="kpi"
        data={kpiData}
        onClickHandlers={{
          info: () => setClickTargetName('KPI_CARD'),
          expand: () => null,
        }}
        optionsOverride={cardHeaderDropdownOptionsOverride.KPI_CARD}
      />
    ),
    RENTAL_CATEGORIES: (
      <RentalCategoriesCard
        kpiData={kpiData}
        handleHideShowCards={handleHideShowCards}
        onClickHandlers={{
          info: () => setClickTargetName('RENTAL_CATEGORIES'),
          expand: () => null,
        }}
        optionsOverride={cardHeaderDropdownOptionsOverride.RENTAL_CATEGORIES}
      />
    ),
    MOST_ACTIVE_HOSTS: (
      <MostActiveHosts
        key="mostActiveHosts"
        mostActiveAccounts={kpiData.latestKpi.topHosts}
        handleHideShowCards={handleHideShowCards}
        onClickHandlers={{
          info: () => setClickTargetName('MOST_ACTIVE_HOSTS'),
          expand: () => null,
        }}
        optionsOverride={cardHeaderDropdownOptionsOverride.MOST_ACTIVE_HOSTS}
      />
    ),
    MOST_ACTIVE_OWNERS: (
      <MostActiveOwners
        key="mostActiveOwners"
        mostActiveAccounts={kpiData.latestKpi.topOwners}
        handleHideShowCards={handleHideShowCards}
        onClickHandlers={{
          info: () => setClickTargetName('MOST_ACTIVE_OWNERS'),
          expand: () => null,
        }}
        optionsOverride={cardHeaderDropdownOptionsOverride.MOST_ACTIVE_OWNERS}
      />
    ),
    TOP_ONLINE_PLATFORMS: (
      <OnlinePlatformsCard
        kpiData={kpiData}
        handleHideShowCards={handleHideShowCards}
        onClickHandlers={{
          info: () => setClickTargetName('TOP_ONLINE_PLATFORMS'),
          expand: () => null,
        }}
        optionsOverride={cardHeaderDropdownOptionsOverride.TOP_ONLINE_PLATFORMS}
      />
    ),
  };

  function hasData(datasetType) {
    const dsKeyName = dataTypeKeyNameMap[datasetType];
    return Boolean(kpiData.latestKpi[dsKeyName]);
  }

  function cardListModalToggler(datasetType) {
    // assumes we already have the data by the time DashCardList is rendered
    const releasing = grid.getItems().filter((item) => item.isReleasing());
    return hasData(datasetType) && releasing.length === 0
      ? setCurrActiveCardListModal(datasetType)
      : null;
  }

  function constructMovableList() {
    const movable = [];
    const HIDE = true;

    Object.entries(movableCards).forEach(([cardName, card], i) =>
      movable.push(
        <GridItem key={i} id={cardName}>
          {card}
        </GridItem>
      )
    );

    movableCharts.forEach((chartName) => {
      const Chart = CHART_TO_COMPONENT[chartName];

      movable.push(
        <GridItem key={`${chartName}-chart`} id={chartName}>
          <CardMD
            growable
            clickable
            onClickHandlers={{
              expand: () => cardListModalToggler(chartName),
              info: () => setClickTargetName(chartName),
            }}
            onClick={() => cardListModalToggler(chartName)}
            onHide={() => handleHideShowCards([chartName], HIDE)}
            optionsOverride={cardHeaderDropdownOptionsOverride[chartName]}
          >
            <Header chartName={chartName} />
            <Chart chartName={chartName} propertyData={kpiData} />
          </CardMD>
        </GridItem>
      );
    });

    return movable;
  }

  useEffect(() => {
    setGrid(getGridInstance());
  }, []);

  useEffect(() => {
    /**
     * This Effect is important. In order to prevent the last item from moving,
     * we must know it's position. Calculating position each time we move
     * causes freezing as the computation takes too long to complete. Here,
     * we calculate only when the grid is created or changed and pass it to
     * Muuri so no calculation is needed.
     */
    if (grid) {
      setNumGridItems(grid.getItems().length);
      // These charts do not have data atm and are to be hidden by default
      // TODO: Need to determine which charts do not have data and hide by default
      // handleHideShowCards(['REGISTRATION_RATE'], true);
    }
  }, [grid, handleHideShowCards]);

  const handleAddChartModalVisibility = () => {
    setAddChartVisibility(!isAddChartModalVisible);
  };

  const handlePrintChartModalVisibility = () => {
    setPrintChartVisibility(!isPrintChartModalVisible);
  };

  function getActiveList() {
    const active = grid
      ? grid.getItems().filter((item) => item.isActive())
      : [];
    const activeList = active.map((activeItem) =>
      activeItem.getElement().getAttribute('id')
    );
    return activeList;
  }

  return (
    <DashCardListWrapper>
      <DashContentActionMenu
        addChartClicked={handleAddChartModalVisibility}
        printTrigger={handlePrintChartModalVisibility}
      />
      <GridWrapper>
        <Grid className="grid">
          {constructMovableList()}
          <GridItem id="ADD_CHART_CARD">
            <AddChartCard onClick={handleAddChartModalVisibility} />
          </GridItem>
        </Grid>
        <AddChartModal
          totAlias={totAlias}
          showHideCards={handleHideShowCards}
          showModal={isAddChartModalVisible}
          activeCards={getActiveList()}
          onHide={handleAddChartModalVisibility}
        />
        <PrintChartModal
          // printContent={printableContent.current}
          showModal={isPrintChartModalVisible}
          kpiData={kpiData}
          activeCards={[]}
          onHide={handlePrintChartModalVisibility}
        />
        <AsyncRsModal
          chartType={currActiveCardListModal}
          onHide={() => setCurrActiveCardListModal(null)}
        />
        <AsyncInformationModal
          isModalActive={Boolean(clickTargetName)}
          hideModal={() => setClickTargetName(null)}
          whichCard={clickTargetName}
        />
      </GridWrapper>
    </DashCardListWrapper>
  );
}

export function DashCardListContainer() {
  const dispatch = useDispatch();
  const kpiData = useSelector(dataSelectors.kpiData);

  useEffect(() => {
    if (!kpiData) {
      dispatch(dataActions.fetchKpiData());
    }
  }, [kpiData, dispatch]);

  if (!kpiData) {
    return <DataLoadingPlaceholder />;
  }

  return <KpiCards kpiData={kpiData} />;
}
